import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Flame, Trophy, Zap, Target, TrendingUp, Star, Lock, BarChart2, CheckCircle2,
} from "lucide-react";
import { BG_IMAGE } from "@/styles/shared";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CARD = {
  background: "rgba(255,255,255,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.7)",
  borderRadius: "0.875rem",
  boxShadow: "0 4px 24px rgba(26,77,46,0.06)",
};

const SECTION_HEADER = {
  padding: "0.875rem 1.25rem",
  borderBottom: "1px solid rgba(26,77,46,0.08)",
  display: "flex", alignItems: "center", gap: "0.5rem",
};

const LABEL_SM = {
  fontFamily: "'Manrope', sans-serif",
  fontSize: "0.72rem", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.07em",
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub, color = "#1A4D2E" }) {
  return (
    <div style={{ ...CARD, padding: "1.1rem 1.25rem", display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.25rem" }}>
        <span style={{ color: "#3d6b1f" }}>{icon}</span>
        <span style={{ ...LABEL_SM, color: "#6b7280" }}>{label}</span>
      </div>
      <span style={{ fontFamily: "'Manrope', sans-serif", color, fontSize: "2rem", fontWeight: 800, lineHeight: 1 }}>
        {value}
      </span>
      {sub && <span style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.78rem" }}>{sub}</span>}
    </div>
  );
}

function ActivityHeatmap({ days }) {
  if (!days?.length) return null;
  const maxCount = Math.max(...days.map((d) => d.tasks_completed), 1);
  const getColor = (count) => {
    if (count === 0) return "rgba(26,77,46,0.07)";
    const i = Math.min(count / maxCount, 1);
    if (i < 0.25) return "rgba(26,77,46,0.25)";
    if (i < 0.5)  return "rgba(26,77,46,0.45)";
    if (i < 0.75) return "rgba(26,77,46,0.65)";
    return "#1A4D2E";
  };
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return (
    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
      {weeks.map((week, wi) => (
        <div key={week[0]?.date ?? wi} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {week.map((day) => (
            <div
              key={day.date}
              title={`${day.date}: ${day.tasks_completed} task${day.tasks_completed !== 1 ? "s" : ""}`}
              style={{ width: "14px", height: "14px", borderRadius: "3px", background: getColor(day.tasks_completed), transition: "transform 0.1s", cursor: "default" }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.3)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function AchievementCard({ ach }) {
  return (
    <div
      data-testid={`achievement-${ach.key}`}
      style={{ ...CARD, padding: "1rem", opacity: ach.unlocked ? 1 : 0.55, filter: ach.unlocked ? "none" : "grayscale(0.4)", position: "relative", transition: "opacity 0.2s, transform 0.15s" }}
      onMouseEnter={(e) => { if (ach.unlocked) e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
    >
      {!ach.unlocked && <div style={{ position: "absolute", top: "0.6rem", right: "0.6rem", color: "#9ca3af" }}><Lock size={12} /></div>}
      <div style={{ fontSize: "1.75rem", marginBottom: "0.4rem" }}>{ach.icon}</div>
      <div style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.82rem", fontWeight: 700, marginBottom: "0.2rem" }}>{ach.title}</div>
      <div style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.72rem", lineHeight: 1.4 }}>{ach.desc}</div>
      {ach.xp > 0 && (
        <div style={{ marginTop: "0.5rem", display: "inline-flex", alignItems: "center", background: ach.unlocked ? "rgba(26,77,46,0.08)" : "rgba(0,0,0,0.05)", color: ach.unlocked ? "#1A4D2E" : "#9ca3af", borderRadius: "2rem", padding: "0.15rem 0.5rem", fontSize: "0.68rem", fontWeight: 700, fontFamily: "'Manrope', sans-serif" }}>
          +{ach.xp} XP
        </div>
      )}
      {ach.unlocked && ach.unlocked_at && (
        <div style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.65rem", marginTop: "0.4rem" }}>
          {new Date(ach.unlocked_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
        </div>
      )}
    </div>
  );
}

// ── Custom tooltip for the trend chart ────────────────────────────────────────
function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ ...CARD, padding: "0.5rem 0.8rem" }}>
      <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.75rem", color: "#6b7280", margin: 0 }}>{label}</p>
      <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.88rem", fontWeight: 700, color: "#1A4D2E", margin: "0.1rem 0 0" }}>
        {payload[0].value} {payload[0].value === 1 ? "task" : "tasks"}
      </p>
    </div>
  );
}

// ── Completion Trend Chart ─────────────────────────────────────────────────────
function CompletionTrend({ days }) {
  if (!days?.length) return null;

  const chartData = days.map((d) => ({
    date: d.date,
    label: new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    tasks: d.tasks_completed,
  }));

  const total = days.reduce((s, d) => s + d.tasks_completed, 0);
  const activeDays = days.filter((d) => d.tasks_completed > 0).length;
  const peak = Math.max(...days.map((d) => d.tasks_completed));

  return (
    <div style={{ ...CARD, overflow: "hidden" }}>
      <div style={SECTION_HEADER}>
        <TrendingUp size={16} color="#3d6b1f" />
        <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>
          Completion Trend — Last 28 Days
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "1.25rem" }}>
          {[
            { label: "Total", value: total },
            { label: "Active Days", value: activeDays },
            { label: "Peak Day", value: peak },
          ].map(({ label, value }) => (
            <div key={label} style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: "1rem", fontWeight: 800, color: "#1A4D2E", lineHeight: 1 }}>{value}</div>
              <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.65rem", color: "#9ca3af", fontWeight: 600 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ padding: "1.25rem 1.25rem 0.75rem" }}>
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#3d6b1f" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#3d6b1f" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,77,46,0.07)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontFamily: "'Manrope', sans-serif", fontSize: 10, fill: "#9ca3af" }}
              tickLine={false} axisLine={false}
              interval={Math.floor(chartData.length / 6)}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontFamily: "'Manrope', sans-serif", fontSize: 10, fill: "#9ca3af" }}
              tickLine={false} axisLine={false}
            />
            <Tooltip content={<TrendTooltip />} />
            <Area
              type="monotone" dataKey="tasks" stroke="#3d6b1f" strokeWidth={2}
              fill="url(#trendFill)" dot={false} activeDot={{ r: 4, fill: "#1A4D2E", strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Goal Progress Summary ─────────────────────────────────────────────────────
const PRIORITY_COLOR = { high: "#dc2626", medium: "#d97706", low: "#6b7280" };
const PRIORITY_BG    = { high: "rgba(220,38,38,0.08)", medium: "rgba(217,119,6,0.08)", low: "rgba(107,114,128,0.08)" };
const STATUS_COLOR   = { completed: "#16a34a", active: "#1A4D2E", archived: "#9ca3af" };

function GoalProgressSummary({ goals }) {
  const active    = goals.filter((g) => g.status === "active");
  const completed = goals.filter((g) => g.status === "completed");

  if (!goals.length) {
    return (
      <div style={{ ...CARD, overflow: "hidden" }}>
        <div style={SECTION_HEADER}>
          <Target size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>Goal Progress</span>
        </div>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.88rem", margin: 0 }}>No goals yet — create one to track progress here.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...CARD, overflow: "hidden" }}>
      <div style={SECTION_HEADER}>
        <Target size={16} color="#3d6b1f" />
        <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>Goal Progress</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "1rem" }}>
          <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", color: "#16a34a", fontWeight: 700 }}>
            {completed.length} complete
          </span>
          <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", color: "#6b7280", fontWeight: 600 }}>
            {active.length} active
          </span>
        </div>
      </div>
      <div style={{ padding: "0.75rem 1.25rem", display: "flex", flexDirection: "column", gap: "0.875rem" }}>
        {goals.map((g) => {
          const pct = g.progress_percentage || 0;
          const totalItems  = g.items?.length || 0;
          const doneItems   = g.items?.filter((i) => i.is_completed).length || 0;

          return (
            <div key={g.id} data-testid={`goal-stat-${g.id}`}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "0.4rem", gap: "0.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", minWidth: 0 }}>
                  <span style={{
                    ...LABEL_SM, color: PRIORITY_COLOR[g.priority] || "#6b7280",
                    background: PRIORITY_BG[g.priority] || "rgba(107,114,128,0.08)",
                    borderRadius: "0.25rem", padding: "0.1rem 0.35rem",
                    flexShrink: 0,
                  }}>
                    {g.priority}
                  </span>
                  <span style={{
                    fontFamily: "'Manrope', sans-serif", fontSize: "0.88rem", fontWeight: 700,
                    color: STATUS_COLOR[g.status] || "#1A4D2E",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {g.title}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                  {g.status === "completed" && <CheckCircle2 size={14} color="#16a34a" />}
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", fontWeight: 800, color: "#1A4D2E" }}>
                    {pct}%
                  </span>
                  {totalItems > 0 && (
                    <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.72rem", color: "#9ca3af" }}>
                      {doneItems}/{totalItems}
                    </span>
                  )}
                </div>
              </div>
              {/* Progress bar */}
              <div style={{ height: "7px", background: "rgba(26,77,46,0.08)", borderRadius: "4px", overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: `${pct}%`,
                  background: g.status === "completed"
                    ? "linear-gradient(90deg, #16a34a, #4ade80)"
                    : "linear-gradient(90deg, #1A4D2E, #86c559)",
                  borderRadius: "4px",
                  transition: "width 0.7s ease",
                }} />
              </div>
              {g.target_date && (
                <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.68rem", color: "#9ca3af", marginTop: "0.2rem" }}>
                  Target: {new Date(g.target_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Per-Project Task Breakdown ────────────────────────────────────────────────
const HEALTH_COLOR = { green: "#16a34a", yellow: "#d97706", red: "#dc2626" };

function ProjectBreakdown({ projects }) {
  if (!projects?.length) {
    return (
      <div style={{ ...CARD, overflow: "hidden" }}>
        <div style={SECTION_HEADER}>
          <BarChart2 size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>Project Breakdown</span>
        </div>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.88rem", margin: 0 }}>No active projects found.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...CARD, overflow: "hidden" }}>
      <div style={SECTION_HEADER}>
        <BarChart2 size={16} color="#3d6b1f" />
        <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>Project Breakdown</span>
        <span style={{ marginLeft: "auto", fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", color: "#6b7280", fontWeight: 600 }}>
          {projects.length} active
        </span>
      </div>
      <div style={{ padding: "0.75rem 1.25rem", display: "flex", flexDirection: "column", gap: "1.1rem" }}>
        {projects.map((p) => {
          const total   = p.total_task_count || 0;
          const done    = p.completed_task_count || 0;
          const active  = p.active_task_count || 0;
          const inProg  = total - done - active;
          const pct     = total > 0 ? Math.round((done / total) * 100) : 0;

          return (
            <div key={p.id} data-testid={`project-stat-${p.id}`}>
              {/* Header row */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.4rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", minWidth: 0 }}>
                  {/* Health dot */}
                  <div style={{
                    width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
                    background: HEALTH_COLOR[p.health] || "#9ca3af",
                  }} title={`Activity: ${p.health}`} />
                  {p.emoji && <span style={{ fontSize: "1rem" }}>{p.emoji}</span>}
                  <span style={{
                    fontFamily: "'Manrope', sans-serif", fontSize: "0.88rem", fontWeight: 700,
                    color: "#1A4D2E", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {p.title}
                  </span>
                </div>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", fontWeight: 800, color: "#1A4D2E", flexShrink: 0 }}>
                  {pct}%
                </span>
              </div>

              {/* Stacked progress bar */}
              <div style={{ height: "8px", background: "rgba(26,77,46,0.07)", borderRadius: "4px", overflow: "hidden", display: "flex" }}>
                {done > 0 && (
                  <div style={{ width: `${(done / Math.max(total, 1)) * 100}%`, background: "#3d6b1f", transition: "width 0.7s ease" }} title={`${done} done`} />
                )}
                {inProg > 0 && (
                  <div style={{ width: `${(inProg / Math.max(total, 1)) * 100}%`, background: "#86c559", transition: "width 0.7s ease" }} title={`${inProg} in progress`} />
                )}
              </div>

              {/* Stats row */}
              <div style={{ display: "flex", gap: "1rem", marginTop: "0.35rem" }}>
                {[
                  { label: "Total",       value: total,  color: "#6b7280" },
                  { label: "Done",        value: done,   color: "#3d6b1f" },
                  { label: "In Progress", value: inProg > 0 ? inProg : 0, color: "#86c559" },
                  { label: "Inbox",       value: active, color: "#9ca3af" },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                    <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.75rem", fontWeight: 700, color }}>{value}</span>
                    <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.68rem", color: "#9ca3af" }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div style={{ padding: "0.6rem 1.25rem", borderTop: "1px solid rgba(26,77,46,0.06)", display: "flex", gap: "1.25rem", flexWrap: "wrap" }}>
        {[
          { color: "#3d6b1f", label: "Done" },
          { color: "#86c559", label: "In Progress" },
          { color: "rgba(26,77,46,0.07)", label: "Inbox" },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "2px", background: color, border: "1px solid rgba(26,77,46,0.12)" }} />
            <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.7rem", color: "#6b7280" }}>{label}</span>
          </div>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: "0.75rem" }}>
          {Object.entries(HEALTH_COLOR).map(([k, c]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: c }} />
              <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.68rem", color: "#9ca3af", textTransform: "capitalize" }}>{k}</span>
            </div>
          ))}
          <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.68rem", color: "#c4b5a0" }}>activity</span>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function StatsPage({ isMobile }) {
  const { token } = useAuth();
  const [profile, setProfile]   = useState(null);
  const [goals, setGoals]       = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    const fetchAll = async () => {
      try {
        const [profileRes, goalsRes, projectsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/gamification/profile`, { headers }),
          axios.get(`${BACKEND_URL}/api/goals`,                { headers }),
          axios.get(`${BACKEND_URL}/api/projects`,             { headers }),
        ]);
        setProfile(profileRes.data);
        setGoals(goalsRes.data);
        setProjects(projectsRes.data);
      } catch (err) { console.error("StatsPage fetch failed:", err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [token]);

  if (loading) {
    return (
      <div style={{ padding: "3rem", textAlign: "center" }}>
        <p style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.9rem" }}>Loading stats...</p>
      </div>
    );
  }
  if (!profile) return null;

  const unlocked = profile.achievements.filter((a) => a.unlocked);
  const locked   = profile.achievements.filter((a) => !a.unlocked);

  return (
    <div data-testid="stats-page" style={{ width: "100%", maxWidth: "920px", display: "flex", flexDirection: "column", gap: "1.25rem" }}>

      {/* Header */}
      <div>
        <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: isMobile ? "1.5rem" : "1.75rem", fontWeight: 800, color: "#1A4D2E", margin: 0, textShadow: "0 1px 3px rgba(255,255,255,0.9)" }}>
          Your Stats
        </h1>
        <p style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.875rem", margin: "0.2rem 0 0" }}>
          Progress, achievements, and streaks
        </p>
      </div>

      {/* Level card */}
      <div data-testid="level-card" style={{ ...CARD, padding: "1.5rem 1.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "rgba(26,77,46,0.1)", border: "2px solid #1A4D2E", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontWeight: 800, fontSize: "1.1rem" }}>{profile.level}</span>
            </div>
            <div>
              <div style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontWeight: 800, fontSize: "1.15rem" }}>Level {profile.level}</div>
              <div style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.8rem" }}>{profile.total_xp.toLocaleString()} total XP</div>
            </div>
          </div>
          <div style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.82rem", textAlign: "right" }}>
            <span style={{ color: "#1A4D2E", fontWeight: 700 }}>{profile.xp_in_level}</span> / {profile.xp_needed} XP to next level
          </div>
        </div>
        <div style={{ height: "10px", background: "rgba(26,77,46,0.1)", borderRadius: "5px", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${profile.level_progress_pct}%`, background: "linear-gradient(90deg, #1A4D2E, #86c559)", borderRadius: "5px", transition: "width 0.8s ease" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.4rem" }}>
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.72rem" }}>{profile.level_progress_pct}%</span>
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "0.875rem" }}>
        <StatCard icon={<Flame size={15} />} label="Current Streak" value={profile.current_streak} sub={profile.current_streak === 1 ? "day" : "days"} color={profile.current_streak >= 7 ? "#d97706" : "#1A4D2E"} />
        <StatCard icon={<Star  size={15} />} label="Best Streak"    value={profile.longest_streak} sub="days all-time" />
        <StatCard icon={<Zap   size={15} />} label="This Week"      value={profile.velocity_this_week} sub={profile.velocity_last_week > 0 ? `vs ${profile.velocity_last_week} last week` : "tasks done"} />
        <StatCard icon={<Target size={15} />} label="Completion Rate" value={`${profile.field_goal_pct}%`} sub={`${profile.total_tasks_completed} tasks completed`} />
      </div>

      {/* Completion trend */}
      <CompletionTrend days={profile.daily_activity} />

      {/* Goal progress summary */}
      <GoalProgressSummary goals={goals} />

      {/* Project breakdown */}
      <ProjectBreakdown projects={projects} />

      {/* Activity heatmap */}
      <div style={{ ...CARD, overflow: "hidden" }}>
        <div style={SECTION_HEADER}>
          <TrendingUp size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>28-Day Activity</span>
        </div>
        <div style={{ padding: "1rem 1.25rem" }}>
          <ActivityHeatmap days={profile.daily_activity} />
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.75rem" }}>
            <span style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.72rem" }}>Less</span>
            {[0.07, 0.25, 0.45, 0.65, 1].map((o) => (
              <div key={o} style={{ width: "12px", height: "12px", borderRadius: "3px", background: `rgba(26,77,46,${o})` }} />
            ))}
            <span style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.72rem" }}>More</span>
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div style={{ ...CARD, overflow: "hidden" }}>
        <div style={SECTION_HEADER}>
          <Trophy size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>Achievements</span>
          <span style={{ marginLeft: "auto", background: "rgba(26,77,46,0.07)", border: "1px solid rgba(26,77,46,0.15)", color: "#6b7280", borderRadius: "2rem", padding: "0.2rem 0.6rem", fontSize: "0.72rem", fontWeight: 600, fontFamily: "'Manrope', sans-serif" }}>
            {unlocked.length} / {profile.achievements.length}
          </span>
        </div>
        <div style={{ padding: "1rem 1.25rem" }}>
          {unlocked.length > 0 && (
            <>
              <p style={{ ...LABEL_SM, color: "#3d6b1f", margin: "0 0 0.75rem" }}>Unlocked</p>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "0.75rem", marginBottom: "1.25rem" }}>
                {unlocked.map((a) => <AchievementCard key={a.key} ach={a} />)}
              </div>
            </>
          )}
          {locked.length > 0 && (
            <>
              <p style={{ ...LABEL_SM, color: "#9ca3af", margin: "0 0 0.75rem" }}>Locked</p>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "0.75rem" }}>
                {locked.map((a) => <AchievementCard key={a.key} ach={a} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
